import {
  AdvanceFeatureData,
  VehicleFinance,
} from "../advanceFeaturesContainer";
import i18n from "locales/i18n";
import { createFormatters } from "locales/format";

const tc = i18n.getFixedT(null, "calculator");
const comparison = (
  key:
    | "semi_trucks"
    | "blue_whales"
    | "elephants"
    | "small_cars"
    | "grizzly_bears"
    | "adult_men",
  count: number,
) =>
  tc(`ghg.${key}`, {
    count: createFormatters(i18n.language).number(count, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
  } as Record<string, unknown>);

export interface CalculateDataProps {
  annualDrivingDistance: number;
  combinedEnergyConsumption: number;
  combinedFuelConsumption: number;
  utilityKWhRate: number;
  fuelPrice: number;
  lifespan: number;
  advanceFeatureData: AdvanceFeatureData;
  selectedIceCO2Emissions: number;
}

export interface GhgData {
  evCarbonFootprint: number;
  evComparisonText: string[];
  iceCarbonFootprint: number;
  iceComparisonText: string[];
}

export interface CalculationData {
  isSet: boolean;
  annualFuelCostSaving: number;
  lifetimeFuelCostSaving: number;
  evAnnualFuelCostSavings: number;
  iceAnnualFuelCostSavings: number;
  evLifetimeFuelCost: number[];
  iceLifetimeFuelCost: number[];
  evChartData: number[];
  iceChartData: number[];
  ghgData: GhgData;
}

// Named export for utility function
const calculateData = (data: CalculateDataProps): CalculationData => {
  const {
    annualDrivingDistance,
    combinedEnergyConsumption: evEfficiency,
    combinedFuelConsumption: iceEfficiency,
    utilityKWhRate,
    fuelPrice,
    lifespan,
    advanceFeatureData,
    selectedIceCO2Emissions: iceCO2Emissions,
  } = data;

  // Total cost of fuel per year for EV and ICE vehicles
  let evAnnualFuelCost = 0;
  let iceAnnualFuelCost = 0;
  // Total cost of fuel per year over set lifetime
  let iceLifetimeFuelCost: number[] = [0];
  let evLifetimeFuelCost: number[] = [0];
  //Total Ev Fuel savings
  let annualFuelCostSaving = 0;
  let lifetimeFuelCostSaving = 0;
  calculateFuelCostData();

  //Financial calculations
  let iceLifetimeVehicleExpense: number[] = [];
  let evLifetimeVehicleExpense: number[] = [];

  //Insurance calculations
  let evAnnualInsuranceCost = 0;
  let iceAnnualInsuranceCost = 0;
  let evLifetimeInsuranceCost: number[] = [];
  let iceLifetimeInsuranceCost: number[] = [];

  //Carbon footprint calculations
  let evLifetimeCarbonFootprint = 0;
  let iceLifetimeCarbonFootprint = 0;
  let evCarbonComparisonText = [""];
  let iceCarbonComparisonText = [""];

  if (data.advanceFeatureData.includeCostOfVehicle) {
    if (data.advanceFeatureData.includeLease) {
      calculateAdvanceFeatureLeasing();
    } else {
      calculateVehicleCost();
    }
  }

  if (data.advanceFeatureData.includeInsurance) {
    calculateInsuranceCost();
  }

  // Calculate cumulative fuel savings over the lifespan
  let evChartData: number[] = sumAndAccumulateArrays(
    evLifetimeFuelCost,
    evLifetimeVehicleExpense,
    evLifetimeInsuranceCost,
  );
  let iceChartData: number[] = sumAndAccumulateArrays(
    iceLifetimeFuelCost,
    iceLifetimeVehicleExpense,
    iceLifetimeInsuranceCost,
  );

  // Calculate carbon footprint
  calculateCarbonFootprint();

  return {
    isSet: true,
    annualFuelCostSaving: annualFuelCostSaving,
    lifetimeFuelCostSaving: lifetimeFuelCostSaving,
    evAnnualFuelCostSavings: evAnnualFuelCost,
    iceAnnualFuelCostSavings: iceAnnualFuelCost,
    evLifetimeFuelCost: evLifetimeFuelCost,
    iceLifetimeFuelCost: iceLifetimeFuelCost,
    evChartData: evChartData,
    iceChartData: iceChartData,
    ghgData: {
      evCarbonFootprint: evLifetimeCarbonFootprint,
      evComparisonText: evCarbonComparisonText,
      iceCarbonFootprint: iceLifetimeCarbonFootprint,
      iceComparisonText: iceCarbonComparisonText,
    },
  };

  function sumAndAccumulateArrays(
    arr1: number[],
    arr2: number[],
    arr3: number[],
  ): number[] {
    const maxLength = Math.max(arr1.length, arr2.length, arr3.length);

    //Sum corresponding elements
    const summedArray: number[] = Array.from(
      { length: maxLength },
      (_, i) =>
        (arr1[i] !== undefined ? arr1[i] : arr1.at(-1) || 0) +
        (arr2[i] !== undefined ? arr2[i] : arr2.at(-1) || 0) +
        (arr3[i] !== undefined ? arr3[i] : arr3.at(-1) || 0),
    );

    return summedArray;
  }

  //handel fuel related calculations
  function calculateFuelCostData() {
    evAnnualFuelCost = parseFloat(
      ((annualDrivingDistance / 100) * evEfficiency * utilityKWhRate).toFixed(
        2,
      ),
    );
    iceAnnualFuelCost = parseFloat(
      ((annualDrivingDistance / 100) * iceEfficiency * fuelPrice).toFixed(2),
    );

    iceLifetimeFuelCost = calculateCumulativeLifetimeCost(
      lifespan,
      iceAnnualFuelCost,
    );
    evLifetimeFuelCost = calculateCumulativeLifetimeCost(
      lifespan,
      evAnnualFuelCost,
    );

    annualFuelCostSaving = parseFloat(
      (iceAnnualFuelCost - evAnnualFuelCost).toFixed(2),
    );

    lifetimeFuelCostSaving = parseFloat(
      (annualFuelCostSaving * lifespan).toFixed(2),
    );
  }

  //calculate carbon footprint
  function calculateCarbonFootprint() {
    // NumberOfKM * (CombinedKWHP100km / 100) * kgCO2e/kWh
    // 20,000 * (21.2 / 100) * 0.038 - Mustang mach e
    const evConsumption = annualDrivingDistance * (evEfficiency / 10) * 0.038;
    const iceConsumption = (annualDrivingDistance * iceCO2Emissions) / 100;

    evLifetimeCarbonFootprint = evConsumption;
    iceLifetimeCarbonFootprint = iceConsumption;
    evCarbonComparisonText = carbonFootprintComparison(
      evLifetimeCarbonFootprint,
    );
    iceCarbonComparisonText = carbonFootprintComparison(
      iceLifetimeCarbonFootprint,
    );
  }

  //Carbon footprint comparison
  function carbonFootprintComparison(carbonFootprint: number): string[] {
    let comparisons = [];

    if (carbonFootprint >= 50000) {
      comparisons.push(comparison("semi_trucks", carbonFootprint / 64000));
    }
    if (carbonFootprint >= 25000) {
      comparisons.push(comparison("blue_whales", carbonFootprint / 25000));
    }
    if (carbonFootprint >= 6000) {
      comparisons.push(comparison("elephants", carbonFootprint / 6400));
    }
    if (carbonFootprint >= 1000) {
      comparisons.push(comparison("small_cars", carbonFootprint / 1100));
    }
    if (carbonFootprint >= 400) {
      comparisons.push(comparison("grizzly_bears", carbonFootprint / 400));
    }
    if (carbonFootprint >= 80) {
      comparisons.push(comparison("adult_men", carbonFootprint / 80));
    }
    return comparisons;
  }

  //calculate vehicle cost
  function calculateVehicleCost() {
    iceLifetimeVehicleExpense = [
      data.advanceFeatureData.iceFinance.vehicleCost,
    ];
    evLifetimeVehicleExpense = [data.advanceFeatureData.evFinance.vehicleCost];
  }

  //calculate insurance cost
  function calculateInsuranceCost() {
    evAnnualInsuranceCost = data.advanceFeatureData.evFinance.insurance * 12;
    iceAnnualInsuranceCost = data.advanceFeatureData.iceFinance.insurance * 12;
    evLifetimeInsuranceCost = calculateCumulativeLifetimeCost(
      data.lifespan,
      evAnnualInsuranceCost,
    );
    iceLifetimeInsuranceCost = calculateCumulativeLifetimeCost(
      data.lifespan,
      iceAnnualInsuranceCost,
    );
  }

  //calculate leasing cost
  function calculateAdvanceFeatureLeasing() {
    //Financial calculations - calculate the monthly payment after down payment. Then calculate the yearly cost
    let iceAnnualVehicleExpense = calculateYearlyCost(
      data.advanceFeatureData.iceFinance.paymentDuration,
      calculateMonthlyPayment(data.advanceFeatureData.iceFinance),
    );
    let evAnnualVehicleExpense = calculateYearlyCost(
      data.advanceFeatureData.evFinance.paymentDuration,
      calculateMonthlyPayment(data.advanceFeatureData.evFinance),
    );

    //Financing cost with down payment
    iceLifetimeVehicleExpense = [
      advanceFeatureData.iceFinance.downPayment,
      ...iceAnnualVehicleExpense.map(
        (value) => value + advanceFeatureData.iceFinance.downPayment,
      ),
    ];
    evLifetimeVehicleExpense = [
      advanceFeatureData.evFinance.downPayment,
      ...evAnnualVehicleExpense.map(
        (value) => value + advanceFeatureData.evFinance.downPayment,
      ),
    ];

    function calculateMonthlyPayment(vehicle: VehicleFinance): number {
      const loanAmount = vehicle.vehicleCost - vehicle.downPayment;
      const monthlyInterestRate = vehicle.interest / 12 / 100;
      const totalMonths = vehicle.paymentDuration;

      if (monthlyInterestRate === 0) {
        return loanAmount === 0 ? 0 : loanAmount / totalMonths;
      }

      return (
        //Loan amortization formula
        (loanAmount * monthlyInterestRate) /
        (1 - Math.pow(1 + monthlyInterestRate, -totalMonths))
      );
    }

    function calculateYearlyCost(monthlyDuration: number, monthlyCost: number) {
      const yearlyCosts: number[] = [];
      let remainingMonths = monthlyDuration;

      let sum = 0;
      for (let year = 0; remainingMonths > 0; year++) {
        const monthsInYear = Math.min(12, remainingMonths);

        sum += parseFloat((monthlyCost * monthsInYear).toFixed(2));
        yearlyCosts.push(sum);
        remainingMonths -= monthsInYear;
      }

      return yearlyCosts;
    }
  }

  function calculateCumulativeLifetimeCost(
    lifespan: number,
    incrementCost: number,
  ): number[] {
    let totalCost = 0;
    let returnData: number[] = [];

    for (let i = 0; i <= lifespan; i++) {
      returnData.push(totalCost);
      totalCost += incrementCost;
    }
    return returnData;
  }
};

const CalculatorFormulas = { calculateData };

// Default export for the main component
export default CalculatorFormulas;
